import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ArrowRight, Eraser, TrendingUp, TrendingDown, ChartColumn } from "lucide-react";

/**
 * Compact analytics block mounted into the reserved `inv-analytics-slot` on
 * the inventory home. Headline numbers + top-5 items by breakage value +
 * per-site bars. Period defaults to the current calendar month.
 */
export default function AnalyticsSlot({ refreshTick, onSeeReports }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/analytics");
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const periodLabel = data?.from ? formatPeriod(data.from, data.to) : "this period";
  const hasData = data && (data.breakage_value > 0 || data.replacement_value > 0 || data.write_offs_count > 0);

  return (
    <section
      data-testid="inv-analytics-block"
      className="bg-white border border-slate-200 rounded-2xl p-5 lg:p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5">
            <ChartColumn size={11} strokeWidth={2.2} /> Period analytics
          </div>
          <div className="text-lg font-semibold mt-1 text-slate-900">Breakage &amp; replacement · {periodLabel}</div>
        </div>
        <button
          data-testid="analytics-see-reports"
          onClick={onSeeReports}
          className="rounded-full px-3.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 shrink-0"
        >
          Full reports <ArrowRight size={12} strokeWidth={2.2} />
        </button>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-stats">
        <Stat testid="stat-breakage" label="Breakage value" value={fmtINR(data?.breakage_value)} loading={loading} accent="red" Icon={Eraser} />
        <Stat testid="stat-replacement" label="Replacement value" value={fmtINR(data?.replacement_value)} loading={loading} accent="olive" Icon={TrendingUp} />
        <Stat testid="stat-net" label="Net movement" value={fmtINR(data?.net_movement)} loading={loading} accent={(data?.net_movement || 0) >= 0 ? "olive" : "red"} Icon={(data?.net_movement || 0) >= 0 ? TrendingUp : TrendingDown} />
        <Stat testid="stat-writeoffs" label="# write-offs" value={data?.write_offs_count ?? 0} loading={loading} accent="slate" />
      </div>

      {!loading && !hasData && (
        <div className="mt-4 rounded-xl border border-dashed border-slate-200 px-4 py-5 text-center" data-testid="analytics-empty">
          <div className="text-sm font-medium text-slate-700">No activity in this period yet</div>
          <p className="text-xs text-slate-500 mt-1">Once you record purchases or write-offs (and set per-item rates), the breakage / replacement figures will populate here.</p>
        </div>
      )}

      {/* Top items by breakage */}
      {hasData && (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div data-testid="top-items-block">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">
              Top items by breakage
            </div>
            {!data?.top_items_by_breakage?.length ? (
              <div className="text-xs text-slate-500 italic">No write-offs valued yet — add rates to value past breakage.</div>
            ) : (
              <ol className="space-y-1.5">
                {data.top_items_by_breakage.map((it, i) => {
                  const max = data.top_items_by_breakage[0].breakage_value || 1;
                  const pct = Math.max(6, Math.round((it.breakage_value / max) * 100));
                  return (
                    <li key={it.item_id} data-testid={`top-item-${i}`} className="">
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="font-medium text-slate-800 truncate pr-2">{it.item_name}</span>
                        <span className="font-semibold text-red-700 tabular-nums shrink-0">{fmtINR(it.breakage_value)}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                        <div className="h-full bg-red-500/80 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <div data-testid="per-site-block">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">
              Per-site breakdown
            </div>
            {!data?.per_site?.length ? (
              <div className="text-xs text-slate-500 italic">No site activity in this period.</div>
            ) : (
              <ol className="space-y-1.5">
                {data.per_site.slice(0, 6).map((s, i) => {
                  const denom = Math.max(
                    ...data.per_site.map((x) => Math.max(x.breakage_value || 0, x.replacement_value || 0)),
                    1
                  );
                  return (
                    <li key={s.site} data-testid={`site-block-${i}`}>
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="font-medium text-slate-800 truncate pr-2">{s.site}</span>
                        <span className="text-[11px] text-slate-500 tabular-nums">
                          <span className="text-olive font-semibold">{fmtINR(s.replacement_value)}</span>
                          {" · "}
                          <span className="text-red-700 font-semibold">{fmtINR(s.breakage_value)}</span>
                        </span>
                      </div>
                      <div className="flex gap-1 mt-1">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex-1">
                          <div
                            className="h-full bg-olive rounded-full"
                            style={{ width: `${Math.round((s.replacement_value / denom) * 100)}%` }}
                          />
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex-1">
                          <div
                            className="h-full bg-red-500/80 rounded-full"
                            style={{ width: `${Math.round((s.breakage_value / denom) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="text-[10px] text-slate-400 mt-2">
              <span className="inline-block w-2 h-2 rounded-full bg-olive mr-1 align-middle"></span> Replacement
              <span className="inline-block w-2 h-2 rounded-full bg-red-500/80 mr-1 ml-3 align-middle"></span> Breakage
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const Stat = ({ testid, label, value, loading, accent = "slate", Icon }) => {
  const tone = {
    olive: "bg-olive/10 text-olive border-olive/30",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[accent] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div data-testid={testid} className={"rounded-2xl border px-4 py-3.5 " + tone}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-80 inline-flex items-center gap-1.5">
        {Icon && <Icon size={11} strokeWidth={2.2} />}
        {label}
      </div>
      <div className="text-2xl font-semibold tracking-tight mt-1 tabular-nums">
        {loading ? "—" : value}
      </div>
    </div>
  );
};

function fmtINR(n) {
  if (n == null) return "—";
  if (typeof n === "number" && Number.isFinite(n)) {
    return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return n;
}

function formatPeriod(fromIso, toIso) {
  try {
    const f = new Date(fromIso);
    const t = new Date(toIso);
    const opts = { day: "numeric", month: "short" };
    return `${f.toLocaleDateString("en-IN", opts)} → ${t.toLocaleDateString("en-IN", opts)}`;
  } catch {
    return "period";
  }
}
